import { useEffect, useState } from "react";
import { api, formatMoney, LOCATIONS, LOCATION_LABEL } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Wallet,
    Plus,
    Minus,
    PencilLine,
    ArrowLeftRight,
    Building2,
    Warehouse,
    EyeOff,
    Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const LOCATION_ICON = {
    club: Building2,
    entrepot: Warehouse,
    cache: EyeOff,
    blanchiment: Sparkles,
};

const LOCATION_ACCENT = {
    club: "bg-[#DDE5D0] text-[#3D4E2E]",
    entrepot: "bg-[#F0E5C7] text-[#7A6018]",
    cache: "bg-slate-200 text-slate-700",
    blanchiment: "bg-sky-100 text-sky-700",
};

export default function Vault() {
    const [balances, setBalances] = useState({
        club: 0,
        entrepot: 0,
        cache: 0,
        blanchiment: 0,
    });
    const [total, setTotal] = useState(0);

    // adjust panel
    const [adjustLocation, setAdjustLocation] = useState("club");
    const [amount, setAmount] = useState("");
    const [note, setNote] = useState("");

    // set panel
    const [setLocation, setSetLocationState] = useState("club");
    const [setValue, setSetValue] = useState("");

    // transfer panel
    const [fromLocation, setFromLocation] = useState("club");
    const [toLocation, setToLocation] = useState("entrepot");
    const [transferAmount, setTransferAmount] = useState("");
    const [transferNote, setTransferNote] = useState("");

    const load = async () => {
        const r = await api.get("/vault");
        setBalances(r.data.balances);
        setTotal(r.data.total);
    };

    useEffect(() => {
        load();
    }, []);

    const adjust = async (sign) => {
        const val = parseFloat(amount);
        if (isNaN(val) || val <= 0) return toast.error("Montant invalide");
        try {
            await api.post("/vault/adjust", {
                amount: sign * val,
                location: adjustLocation,
                note: note || (sign > 0 ? "Dépôt manuel" : "Retrait manuel"),
            });
            toast.success(sign > 0 ? "Dépôt enregistré" : "Retrait enregistré");
            setAmount("");
            setNote("");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        }
    };

    const setExact = async () => {
        const val = parseFloat(setValue);
        if (isNaN(val)) return toast.error("Montant invalide");
        try {
            await api.post("/vault/set", {
                amount: val,
                location: setLocation,
                note: "",
            });
            toast.success("Solde mis à jour");
            setSetValue("");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        }
    };

    const transfer = async () => {
        const val = parseFloat(transferAmount);
        if (isNaN(val) || val <= 0) return toast.error("Montant invalide");
        if (fromLocation === toLocation)
            return toast.error("Choisissez deux emplacements différents");
        try {
            await api.post("/vault/transfer", {
                from_location: fromLocation,
                to_location: toLocation,
                amount: val,
                note: transferNote || "",
            });
            toast.success("Transfert effectué");
            setTransferAmount("");
            setTransferNote("");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        }
    };

    return (
        <>
            <PageHeader
                title="Coffres"
                subtitle="Argent liquide réparti entre le club, l'entrepôt, la planque et le blanchiment"
            />
            <div className="p-8 space-y-8">
                {/* Total + 4 locations */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                    <Card
                        data-testid="vault-total-card"
                        className="border-slate-200 shadow-sm p-6 bg-[#2E3A22] text-white"
                    >
                        <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">
                            Total consolidé
                        </div>
                        <div className="font-display text-4xl font-bold tracking-tighter tabular-nums mt-3">
                            {formatMoney(total)}
                        </div>
                        <div className="text-xs text-slate-400 mt-3">
                            Somme des 4 emplacements
                        </div>
                    </Card>
                    {LOCATIONS.map((loc) => {
                        const Icon = LOCATION_ICON[loc.key];
                        return (
                            <Card
                                key={loc.key}
                                data-testid={`vault-card-${loc.key}`}
                                className="border-slate-200 shadow-sm p-6 bg-white"
                            >
                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                            {loc.label}
                                        </div>
                                        <div className="font-display text-3xl font-bold tracking-tighter text-slate-900 tabular-nums mt-3">
                                            {formatMoney(balances[loc.key])}
                                        </div>
                                    </div>
                                    <div
                                        className={`w-10 h-10 rounded-md flex items-center justify-center ${LOCATION_ACCENT[loc.key]}`}
                                    >
                                        <Icon className="w-5 h-5" />
                                    </div>
                                </div>
                            </Card>
                        );
                    })}
                </div>

                {/* Adjust + Set + Transfer */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Card className="border-slate-200 shadow-sm p-6">
                        <h2 className="font-display text-lg font-semibold text-slate-900 mb-1">
                            Dépôt / Retrait
                        </h2>
                        <p className="text-sm text-slate-500 mb-5">
                            Ajustement manuel sur un emplacement (hors ventes/achats).
                        </p>
                        <div className="space-y-4">
                            <div>
                                <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                    Emplacement
                                </Label>
                                <Select
                                    value={adjustLocation}
                                    onValueChange={setAdjustLocation}
                                >
                                    <SelectTrigger
                                        data-testid="vault-adjust-location-select"
                                        className="mt-2 h-11"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {LOCATIONS.map((l) => (
                                            <SelectItem key={l.key} value={l.key}>
                                                {l.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                    Montant
                                </Label>
                                <Input
                                    data-testid="vault-adjust-amount-input"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    className="mt-2 h-11"
                                    placeholder="0"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                    Note
                                </Label>
                                <Textarea
                                    data-testid="vault-adjust-note-input"
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    className="mt-2"
                                    rows={2}
                                    placeholder="Racket, salaire, achat véhicule…"
                                />
                            </div>
                            <div className="flex gap-3">
                                <Button
                                    onClick={() => adjust(1)}
                                    data-testid="vault-deposit-btn"
                                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Dépôt
                                </Button>
                                <Button
                                    onClick={() => adjust(-1)}
                                    data-testid="vault-withdraw-btn"
                                    variant="outline"
                                    className="flex-1 border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                                >
                                    <Minus className="w-4 h-4 mr-2" />
                                    Retrait
                                </Button>
                            </div>
                        </div>
                    </Card>

                    <Card className="border-slate-200 shadow-sm p-6">
                        <h2 className="font-display text-lg font-semibold text-slate-900 mb-1">
                            Transfert
                        </h2>
                        <p className="text-sm text-slate-500 mb-5">
                            Déplacer de l&apos;argent d&apos;un emplacement à un autre.
                        </p>
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                        De
                                    </Label>
                                    <Select
                                        value={fromLocation}
                                        onValueChange={setFromLocation}
                                    >
                                        <SelectTrigger
                                            data-testid="vault-transfer-from-select"
                                            className="mt-2 h-11"
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {LOCATIONS.map((l) => (
                                                <SelectItem key={l.key} value={l.key}>
                                                    {l.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                        Vers
                                    </Label>
                                    <Select
                                        value={toLocation}
                                        onValueChange={setToLocation}
                                    >
                                        <SelectTrigger
                                            data-testid="vault-transfer-to-select"
                                            className="mt-2 h-11"
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {LOCATIONS.map((l) => (
                                                <SelectItem key={l.key} value={l.key}>
                                                    {l.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                    Montant
                                </Label>
                                <Input
                                    data-testid="vault-transfer-amount-input"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={transferAmount}
                                    onChange={(e) => setTransferAmount(e.target.value)}
                                    className="mt-2 h-11"
                                    placeholder="0"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                    Note
                                </Label>
                                <Textarea
                                    data-testid="vault-transfer-note-input"
                                    value={transferNote}
                                    onChange={(e) => setTransferNote(e.target.value)}
                                    className="mt-2"
                                    rows={2}
                                    placeholder="Raison du transfert…"
                                />
                            </div>
                            <Button
                                onClick={transfer}
                                data-testid="vault-transfer-btn"
                                className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                            >
                                <ArrowLeftRight className="w-4 h-4 mr-2" />
                                Transférer{" "}
                                {LOCATION_LABEL[fromLocation]} →{" "}
                                {LOCATION_LABEL[toLocation]}
                            </Button>
                        </div>
                    </Card>

                    <Card className="border-slate-200 shadow-sm p-6">
                        <h2 className="font-display text-lg font-semibold text-slate-900 mb-1">
                            Définir le solde exact
                        </h2>
                        <p className="text-sm text-slate-500 mb-5">
                            Écrase la valeur d&apos;un emplacement. Utile en cas de correction.
                        </p>
                        <div className="space-y-4">
                            <div>
                                <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                    Emplacement
                                </Label>
                                <Select
                                    value={setLocation}
                                    onValueChange={setSetLocationState}
                                >
                                    <SelectTrigger
                                        data-testid="vault-set-location-select"
                                        className="mt-2 h-11"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {LOCATIONS.map((l) => (
                                            <SelectItem key={l.key} value={l.key}>
                                                {l.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                    Nouveau solde
                                </Label>
                                <Input
                                    data-testid="vault-set-input"
                                    type="number"
                                    step="0.01"
                                    value={setValue}
                                    onChange={(e) => setSetValue(e.target.value)}
                                    className="mt-2 h-11"
                                    placeholder="0"
                                />
                            </div>
                            <Button
                                onClick={setExact}
                                data-testid="vault-set-btn"
                                className="w-full bg-[#3D4E2E] hover:bg-[#2E3A22] text-white"
                            >
                                <PencilLine className="w-4 h-4 mr-2" />
                                Écraser le solde
                            </Button>
                        </div>
                    </Card>
                </div>
            </div>
        </>
    );
}
